import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Wallet,
  Plus,
  Fuel,
  Package,
  Users as UsersIcon,
  Receipt,
  CircleDollarSign,
  Trash2,
  TrendingUp,
  TrendingDown,
  Loader2,
  AlertTriangle,
  CreditCard,
  ChevronRight,
  Wrench,
  HardHat,
  UtensilsCrossed,
} from "lucide-react";
import { toast } from "sonner";
import { api, BRL, formatDateBR } from "@/lib/api";
import { Link } from "react-router-dom";
import { PayDialog } from "@/pages/FinanceCartao";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MoneyInput from "@/components/MoneyInput";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import PageHeader from "@/components/PageHeader";
import MonthPicker from "@/components/MonthPicker";
import { Checkbox } from "@/components/ui/checkbox";
import SearchableCombobox from "@/components/SearchableCombobox";
import { ReceiptField, ReceiptInline } from "@/components/ReceiptAttach";
import { CashflowChart } from "@/components/CashflowChart";

export const CATEGORY_META = {
  gasolina:        { label: "Gasolina / Combustível", icon: Fuel,            color: "bg-amber-100 text-amber-600" },
  material_escola: { label: "Material por escola",    icon: Package,         color: "bg-sky-100 text-sky-600" },
  funcionarios:    { label: "Funcionários",            icon: UsersIcon,       color: "bg-emerald-100 text-emerald-600" },
  locacao:         { label: "Locação de equipamentos", icon: Package,         color: "bg-violet-100 text-violet-600" },
  servicos_terceirizados: { label: "Serviços terceirizados", icon: HardHat,  color: "bg-orange-100 text-orange-600" },
  ferramentas:     { label: "Ferramentas",             icon: Wrench,          color: "bg-cyan-100 text-cyan-700" },
  alimentacao:     { label: "Alimentação",             icon: UtensilsCrossed, color: "bg-lime-100 text-lime-700" },
  impostos:        { label: "Impostos",                icon: Receipt,         color: "bg-rose-100 text-rose-600" },
  outros:          { label: "Outros",                  icon: CircleDollarSign,color: "bg-slate-100 text-slate-600" },
};

const TODAY = new Date().toISOString().slice(0, 10);
const THIS_MONTH = TODAY.slice(0, 7);

function monthLabel(ym) {
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("pt-BR", { month: "short", year: "2-digit" }).replace(".", "");
}

export default function Financeiro() {
  const [tab, setTab] = useState("fluxo");
  const [cashflow, setCashflow] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [schools, setSchools] = useState([]);
  const [month, setMonth] = useState(THIS_MONTH);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cf, sch, exp] = await Promise.all([
        api.get("/financeiro/cashflow?months=12"),
        api.get("/schools"),
        api.get(`/expenses?month=${month}`),
      ]);
      setCashflow(cf.data);
      setSchools(sch.data || []);
      setExpenses(exp.data || []);
    } catch (e) {
      toast.error("Falha ao carregar financeiro");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [month]); // eslint-disable-line

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Wallet}
        title="Financeiro"
        subtitle="Receitas, impostos, despesas e fluxo de caixa"
        iconColor="bg-emerald-500"
      />

      <CashflowChart />

      {/* Totals */}
      {cashflow && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Totalizer
            label={`Receita (${cashflow.period_label || "12m"})`}
            value={cashflow.totals.revenue}
            color="bg-emerald-500"
            icon={TrendingUp}
            testid="fin-tot-revenue"
          />
          <Totalizer
            label="Impostos (ISS + Simples)"
            value={cashflow.totals.taxes}
            color="bg-rose-500"
            icon={Receipt}
            testid="fin-tot-taxes"
            negative
          />
          <Totalizer
            label={`Despesas (${cashflow.period_label || "12m"})`}
            value={cashflow.totals.expenses_total}
            color="bg-amber-500"
            icon={TrendingDown}
            testid="fin-tot-expenses"
            negative
          />
          <Totalizer
            label={`Lucro líquido (${cashflow.period_label || "12m"})`}
            value={cashflow.totals.net}
            color="bg-[#7c3aed]"
            icon={CircleDollarSign}
            testid="fin-tot-net"
            emphasize
          />
        </div>
      )}
      {cashflow?.totals?.courtesy_count > 0 && <CourtesyLine c={cashflow.totals} />}

      {/* Cartão de crédito — pagamento da fatura direto do painel */}
      <CardInvoiceBanner />

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        {[
          { k: "fluxo",    l: "Fluxo de caixa" },
          { k: "despesas", l: "Despesas" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`fin-tab-${t.k}`}
            className={`px-4 py-2.5 text-sm font-bold border-b-2 -mb-px transition-colors ${
              tab === t.k
                ? "border-[#7c3aed] text-[#7c3aed]"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : tab === "fluxo" ? (
        <CashflowTab cashflow={cashflow} />
      ) : (
        <ExpensesTab
          expenses={expenses}
          month={month}
          setMonth={setMonth}
          schools={schools}
          onAdd={() => setShowForm(true)}
          onReload={load}
        />
      )}

      <ExpenseForm
        open={showForm}
        onOpenChange={setShowForm}
        schools={schools}
        onSaved={async () => { setShowForm(false); await load(); }}
      />
    </div>
  );
}

/* ---------------- Totalizer ---------------- */
const Totalizer = ({ label, value, color, icon: Icon, testid, emphasize = false, negative = false }) => (
  <div
    data-testid={testid}
    className={`rounded-2xl p-3 md:p-4 border bg-white card-shadow ${
      emphasize ? "border-[#7c3aed]/30 ring-2 ring-[#7c3aed]/10" : "border-slate-100"
    }`}
  >
    <div className="flex items-center gap-2 min-w-0">
      <div className={`w-8 h-8 md:w-9 md:h-9 rounded-lg ${color} text-white flex items-center justify-center shrink-0`}>
        <Icon className="w-4 h-4 md:w-4.5 md:h-4.5" strokeWidth={2.3} />
      </div>
      <p className="text-[10px] md:text-[11px] uppercase tracking-wider font-bold text-slate-500 leading-tight line-clamp-2 min-w-0">
        {label}
      </p>
    </div>
    <p className={`mt-2 font-heading font-black text-lg md:text-2xl tabular-nums break-words ${
      emphasize ? (value >= 0 ? "text-[#1e3a8a]" : "text-rose-600") : "text-slate-900"
    }`}>
      {negative && value > 0 ? "- " : ""}{BRL(value)}
    </p>
  </div>
);

/* ---------------- Cashflow tab ---------------- */
const CourtesyLine = ({ c }) => (
  <div className="rounded-xl border border-pink-200 bg-pink-50 px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11.5px] text-pink-900" data-testid="fin-courtesy-line">
    <span className="font-black">Cortesias ({c.courtesy_count})</span>
    <span>Notas: <b className="tabular-nums">{BRL(c.courtesy_total)}</b></span>
    <span>Impostos: <b className="tabular-nums">{BRL(c.courtesy_taxes)}</b></span>
    <span className="text-[10.5px] text-pink-700">fora da receita e dos sócios — entram só no DAS</span>
  </div>
);

const MonthCard = ({ m, maxV }) => {
  const out = m.taxes + m.expenses_total;
  return (
    <li data-testid={`cashrow-${m.month}`}>
      <Link to={`/financeiro/mensal?month=${m.month}`} data-testid={`cashrow-open-${m.month}`}
        className="block rounded-2xl border border-slate-100 bg-white card-shadow p-4 transition-colors hover:border-[#7c3aed]/40 active:bg-slate-50">
      <div className="flex items-center justify-between mb-2">
        <p className="font-heading font-black text-[#1e3a8a] capitalize">{m.label}</p>
        <p className={`font-black tabular-nums flex items-center gap-1 ${m.net >= 0 ? "text-[#1e3a8a]" : "text-rose-600"}`}>{BRL(m.net)} <ChevronRight className="w-4 h-4 text-slate-400" /></p>
      </div>
      <div className="space-y-1.5">
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${(m.revenue / maxV) * 100}%` }} /></div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-rose-500" style={{ width: `${(out / maxV) * 100}%` }} /></div>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
        <div><p className="text-slate-500">Receita</p><p className="font-bold text-emerald-700 tabular-nums">{BRL(m.revenue)}</p></div>
        <div><p className="text-slate-500">Impostos</p><p className="font-bold text-rose-600 tabular-nums">{BRL(m.taxes)}</p></div>
        <div><p className="text-slate-500">Despesas</p><p className="font-bold text-amber-700 tabular-nums">{BRL(m.expenses_total)}</p></div>
      </div>
      {m.courtesy_count > 0 && (
        <p className="mt-2 text-[10.5px] text-pink-700" data-testid={`cashrow-courtesy-${m.month}`}>
          + {m.courtesy_count} cortesia(s): {BRL(m.courtesy_total)} • impostos {BRL(m.courtesy_taxes)} (só DAS)
        </p>
      )}
      <p className="mt-2 text-[10.5px] font-bold text-[#7c3aed]">Ver relatório completo do mês</p>
      </Link>
    </li>
  );
};

const CashflowTab = ({ cashflow }) => {
  if (!cashflow) return null;
  const months = cashflow.months || [];
  const maxV = Math.max(1, ...months.map((m) => Math.max(m.revenue, m.expenses_total + m.taxes)));
  return (
    <div className="space-y-3" data-testid="fin-cashflow">
      <p className="text-xs text-slate-500">
        Mês mais recente primeiro. Conta só orçamentos com <b>Nota paga</b> (receita, impostos e gastos do orçamento) + despesas gerais do mês.
        <span className="ml-2 inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-emerald-500" /> receita</span>
        <span className="ml-2 inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-rose-500" /> impostos + despesas</span>
      </p>
      <ul className="grid gap-3 md:grid-cols-2">
        {months.map((m) => <MonthCard key={m.month} m={m} maxV={maxV} />)}
      </ul>
    </div>
  );
};

/* ---------------- Expenses tab ---------------- */
const ExpensesTab = ({ expenses, month, setMonth, schools, onAdd, onReload }) => {
  const schoolMap = useMemo(() => {
    const m = {};
    for (const s of schools) m[s.id] = s;
    return m;
  }, [schools]);

  const totals = useMemo(() => {
    const by = {};
    let total = 0;
    // Informative expenses are kept in the list for history but EXCLUDED
    // from the "Total do mês" and category totals, mirroring the backend
    // recompute (they must never alter the consolidated finance view).
    for (const e of expenses) {
      if (e.informative) continue;
      by[e.category] = (by[e.category] || 0) + Number(e.amount || 0);
      total += Number(e.amount || 0);
    }
    return { by, total };
  }, [expenses]);

  const remove = async (id) => {
    if (!window.confirm("Remover esta despesa?")) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Despesa removida");
      onReload();
    } catch (e) {
      toast.error("Falha ao remover");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 flex-wrap min-w-0 flex-1">
          <label className="text-xs font-bold text-slate-500 shrink-0">Mês:</label>
          <div className="flex-1 min-w-[200px]">
            <MonthPicker value={month} onChange={setMonth} testid="exp-month" />
          </div>
        </div>
        <Button onClick={onAdd} className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white" data-testid="exp-new-btn">
          <Plus className="w-4 h-4 mr-1" /> Nova despesa
        </Button>
      </div>

      {/* Category chips */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {Object.entries(CATEGORY_META).map(([k, meta]) => (
          <div key={k} className="rounded-xl bg-white border border-slate-100 p-3 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg ${meta.color} flex items-center justify-center shrink-0`}>
              <meta.icon className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 truncate">{meta.label}</p>
              <p className="font-black text-slate-900 text-sm">{BRL(totals.by[k] || 0)}</p>
            </div>
          </div>
        ))}
      </div>

      {/* List */}
      {expenses.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center text-sm text-slate-500">
          Nenhuma despesa lançada em <b>{monthLabel(month)}</b>.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <tr>
                <th className="text-left py-3 px-4 font-semibold">Data</th>
                <th className="text-left py-3 px-4 font-semibold">Categoria</th>
                <th className="text-left py-3 px-4 font-semibold">Descrição</th>
                <th className="text-left py-3 px-4 font-semibold">Escola</th>
                <th className="text-right py-3 px-4 font-semibold">Valor</th>
                <th className="text-right py-3 px-4 font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {expenses.map((e) => {
                const meta = CATEGORY_META[e.category] || CATEGORY_META.outros;
                const Icon = meta.icon;
                const isInfo = !!e.informative;
                return (
                  <tr key={e.id} data-testid={`exp-row-${e.id}`} className={`hover:bg-slate-50 ${isInfo ? "text-slate-400" : ""}`}>
                    <td className="py-3 px-4">{e.date.split("-").reverse().join("/")}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-bold ${isInfo ? "bg-slate-100 text-slate-500" : meta.color}`}>
                        <Icon className="w-3 h-3" />
                        {meta.label}
                      </span>
                      {e.payment_method === "dia_a_dia" && (
                        <span data-testid={`exp-dia-badge-${e.id}`} className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 align-middle">dia a dia</span>
                      )}
                      {e.payment_method === "cartao" && (
                        <span data-testid={`exp-card-badge-${e.id}`} className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-100 text-indigo-700 align-middle">
                          <CreditCard className="w-3 h-3" /> cartão{e.installments > 1 ? ` ${e.installments}x` : ""}
                        </span>
                      )}
                      {isInfo && (
                        <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700 align-middle">
                          {e.illustrative ? "só ilustração" : "informativo"}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {e.description || "—"}
                      <div><ReceiptInline kind="expense" id={e.id} path={e.receipt_path} onDone={onReload} testid={`exp-receipt-${e.id}`} /></div>
                    </td>
                    <td className="py-3 px-4">{e.school_id ? (schoolMap[e.school_id]?.name || "—") : "—"}</td>
                    <td className={`py-3 px-4 text-right font-bold ${isInfo ? "text-slate-400 line-through" : "text-rose-700"}`}>- {BRL(e.amount)}</td>
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => remove(e.id)}
                        data-testid={`exp-delete-${e.id}`}
                        className="text-rose-600 hover:text-rose-700"
                        aria-label="Remover"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-slate-50 font-black">
                <td colSpan={4} className="py-3 px-4 text-right text-slate-700">Total do mês</td>
                <td className="py-3 px-4 text-right text-rose-700">- {BRL(totals.total)}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ---------------- Expense form dialog ---------------- */
// Statuses that count as "live work" (user wants to track real expenses here).
const LIVE_BUDGET_STATUSES = ["aprovado", "iniciando", "finalizado"];
// Post-NF statuses — allowed only in informative mode (never alter calcs).
const POST_NF_BUDGET_STATUSES = ["nota_fiscal_gerada", "nota_faturada"];

const BUDGET_STATUS_LABEL = {
  aprovado: "Aprovado",
  iniciando: "Em andamento",
  finalizado: "Finalizado",
  nota_fiscal_gerada: "Nota gerada",
  nota_faturada: "Nota paga",
};

/** Lightweight searchable combobox used for school / budget pickers. */
// SearchableCombobox lives in /components/SearchableCombobox.jsx so the
// Agenda modal can reuse the exact same UX.
const SCHOOL_CATEGORIES = ["material_escola", "locacao"];
const CARD_CATEGORIES = ["material_escola", "locacao", "gasolina", "outros", "servicos_terceirizados", "ferramentas", "alimentacao"];
const DESC_PLACEHOLDER = {
  gasolina: "Ex.: Abastecimento Van 10/10",
  material_escola: "Ex.: Tinta, cimento, parafusos",
  funcionarios: "Ex.: Diária João 10/10",
  locacao: "Ex.: Aluguel de andaime",
  impostos: "Ex.: Taxa de alvará",
  outros: "Descrição da despesa",
};

const CategoryPicker = ({ value, onChange }) => (
  <div>
    <label className="block text-xs font-bold text-slate-600 mb-1">Categoria</label>
    <div className="grid grid-cols-3 gap-2" data-testid="exp-category">
      {Object.entries(CATEGORY_META).map(([k, m]) => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          data-testid={`exp-cat-${k}`}
          className={`flex flex-col items-center gap-1 rounded-xl border px-1.5 py-2 text-[10.5px] font-bold leading-tight text-center transition-colors ${
            value === k ? "border-[#7c3aed] bg-[#7c3aed]/5 text-[#7c3aed] ring-2 ring-[#7c3aed]/15" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
          }`}
        >
          <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${m.color}`}><m.icon className="w-3.5 h-3.5" /></span>
          {m.label}
        </button>
      ))}
    </div>
  </div>
);

const CardInvoiceBanner = () => {
  const [inv, setInv] = useState(undefined);
  const [pay, setPay] = useState(null);
  const load = () => api.get("/finance/cartao").then((r) => {
    const list = (r.data?.invoices || []).filter((i) => !i.paid && i.total > 0);
    setInv(list.sort((a, b) => a.month.localeCompare(b.month))[0] || null);
  }).catch(() => setInv(null));
  useEffect(() => { load(); }, []);
  if (inv === undefined) return null;
  if (!inv) return (
    <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-4 flex items-center gap-3" data-testid="fin-card-banner">
      <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center shrink-0"><CreditCard className="w-5 h-5 text-white" /></div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-black text-slate-900">Fatura do cartão</p>
        <p className="text-[11px] text-slate-600">Nenhuma fatura em aberto.</p>
      </div>
      <Link to="/financeiro/cartao" className="text-xs font-bold text-indigo-700 underline" data-testid="fin-card-banner-link">Ver faturas</Link>
    </div>
  );
  const canPay = TODAY >= inv.due_date;
  return (
    <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="fin-card-banner">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center shrink-0"><CreditCard className="w-5 h-5 text-white" /></div>
        <div className="min-w-0">
          <p className="text-sm font-black text-slate-900">Fatura do cartão — vence {formatDateBR(inv.due_date)}</p>
          <p className="text-[11px] text-slate-600">{inv.items.length} lançamento(s) • ao pagar, sai do Caixa da empresa</p>
        </div>
      </div>
      <div className="flex items-center gap-2 justify-between sm:justify-end">
        <span className="text-lg font-black tabular-nums text-indigo-700" data-testid="fin-card-banner-total">{BRL(inv.total)}</span>
        <Link to="/financeiro/cartao" className="text-xs font-bold text-indigo-700 underline px-1" data-testid="fin-card-banner-link">Ver</Link>
        <Button onClick={() => setPay(inv)} disabled={!canPay} title={canPay ? "" : `Disponível a partir de ${formatDateBR(inv.due_date)}`}
          className="h-9 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded-xl text-xs font-black" data-testid="fin-card-banner-pay">
          Pagar fatura
        </Button>
      </div>
      <PayDialog target={pay} onClose={() => setPay(null)} onDone={() => { setPay(null); load(); }} />
    </div>
  );
};

export const PaymentMethodField = ({ form, setForm, caixaHint, withDiaADia = false }) => {
  const amt = parseFloat((form.amount || "").toString().replace(",", ".")) || 0;
  const n = Number(form.installments) || 1;
  const opts = [["caixa", "Caixa da empresa"], ...(withDiaADia ? [["dia_a_dia", "Caixa do dia a dia"]] : []), ["cartao", "Cartão de crédito"]];
  return (
    <div>
      <label className="block text-xs font-bold text-slate-600 mb-1">Forma de pagamento</label>
      <div className="flex flex-col gap-2">
        {opts.map(([k, l]) => (
          <button
            key={k}
            type="button"
            onClick={() => setForm({ ...form, payment_method: k, installments: 1 })}
            data-testid={`exp-pay-${k}`}
            className={`h-10 w-full rounded-md border text-sm font-bold transition-colors ${form.payment_method === k ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}
          >
            {k === "cartao" && <CreditCard className="w-3.5 h-3.5 inline mr-1" />}{l}
          </button>
        ))}
      </div>
      {form.payment_method === "cartao" && (
        <div className="mt-3">
          <label className="block text-xs font-bold text-slate-600 mb-1">Parcelas</label>
          <Select value={String(n)} onValueChange={(v) => setForm({ ...form, installments: Number(v) })}>
            <SelectTrigger className="w-full h-10" data-testid="exp-installments"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((i) => (
                <SelectItem key={i} value={String(i)}>{i}x</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {amt > 0 && <p className="text-[11px] text-slate-500 mt-1" data-testid="exp-installment-value">{n}x de {BRL(amt / n)}</p>}
        </div>
      )}
      {form.payment_method === "caixa" && caixaHint && (
        <p className="text-[10px] text-slate-500 mt-1 leading-snug" data-testid="exp-caixa-hint">{caixaHint}</p>
      )}
      {form.payment_method === "dia_a_dia" && (
        <p className="text-[10px] text-slate-500 mt-1 leading-snug" data-testid="exp-dia-hint">Sai da Conta do Dia a Dia agora (aparece no extrato do Dia a Dia). O Caixa da empresa não muda.</p>
      )}
      {form.payment_method === "cartao" && (
        <p className="text-[10px] text-slate-500 mt-1 leading-snug">
          Não sai do Caixa agora: fica como histórico e entra na fatura. Só sai do Caixa quando a fatura (dia 27) for paga. Compras até dia 19 → fatura do mês; a partir do dia 20 → mês seguinte.
        </p>
      )}
    </div>
  );
};

const ExpenseForm = ({ open, onOpenChange, schools, onSaved }) => {
  const [form, setForm] = useState({ date: TODAY, category: "gasolina", amount: "", description: "", school_id: "", budget_id: "", informative: false, payment_method: "caixa", installments: 1, receipt_path: "" });
  const [saving, setSaving] = useState(false);
  const [budgetOptions, setBudgetOptions] = useState([]);
  const [eligibleSchoolIds, setEligibleSchoolIds] = useState(null); // null = still loading
  const [showPostNF, setShowPostNF] = useState(false);              // "Ver NF já gerada" toggle

  useEffect(() => {
    if (open) {
      setForm({ date: TODAY, category: "gasolina", amount: "", description: "", school_id: "", budget_id: "", informative: false, payment_method: "caixa", installments: 1, receipt_path: "" });
      setBudgetOptions([]);
      setShowPostNF(false);
    }
  }, [open]);

  // Load schools that have at least one budget in the chosen status set.
  // When the user flips `showPostNF`, we expand the set so the picker also
  // includes schools whose only live budgets are post-NF (so the user can
  // reach them to add an informative note).
  useEffect(() => {
    if (!open) return;
    const statuses = showPostNF
      ? [...LIVE_BUDGET_STATUSES, ...POST_NF_BUDGET_STATUSES].join(",")
      : LIVE_BUDGET_STATUSES.join(",");
    (async () => {
      try {
        const r = await api.get("/finance/schools-with-budgets", { params: { statuses } });
        setEligibleSchoolIds(new Set((r.data || []).map((s) => s.id)));
      } catch {
        setEligibleSchoolIds(new Set());
      }
    })();
  }, [open, showPostNF]);

  // When a school is picked, fetch its budgets filtered by status.
  useEffect(() => {
    const sid = form.school_id;
    if (!sid) { setBudgetOptions([]); return; }
    const status_in = showPostNF
      ? [...LIVE_BUDGET_STATUSES, ...POST_NF_BUDGET_STATUSES].join(",")
      : LIVE_BUDGET_STATUSES.join(",");
    (async () => {
      try {
        const r = await api.get("/budgets", { params: { school_id: sid, status_in, limit: 100 } });
        setBudgetOptions(r.data || []);
      } catch {
        setBudgetOptions([]);
      }
    })();
  }, [form.school_id, showPostNF]);

  const needsSchool = form.category === "material_escola";
  const showSchool = SCHOOL_CATEGORIES.includes(form.category);
  const showPayment = CARD_CATEGORIES.includes(form.category);
  const canLinkBudget = showSchool && !!form.school_id;

  // Filter school list: only schools present in the eligible set (based on
  // whether the user wants to see post-NF budgets too).
  const schoolOptions = useMemo(() => {
    const base = schools || [];
    if (!eligibleSchoolIds) return []; // still loading
    const filtered = base.filter((s) => eligibleSchoolIds.has(s.id));
    return filtered.map((s) => ({ value: s.id, label: s.name, sub: s.nickname || "" }));
  }, [schools, eligibleSchoolIds]);

  const budgetOpts = useMemo(() => {
    return budgetOptions.map((b) => {
      const isPostNF = POST_NF_BUDGET_STATUSES.includes(b.status);
      const statusTag = BUDGET_STATUS_LABEL[b.status] || b.status;
      return {
        value: b.id,
        label: `Nº ${b.number} — ${b.service_type || "orçamento"}`,
        sub: `${isPostNF ? "⚠ Nota já gerada • " : ""}${statusTag} • R$ ${Number(b.total || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
        _status: b.status,
        _isPostNF: isPostNF,
      };
    });
  }, [budgetOptions]);

  const selectedBudget = budgetOpts.find((b) => b.value === form.budget_id);
  const selectedIsPostNF = !!selectedBudget?._isPostNF;

  // Auto-check "informative" when the user picks a post-NF budget. User
  // can still uncheck it, but we leave it ON by default to protect the
  // consolidated figures.
  useEffect(() => {
    if (!form.budget_id && form.informative) {
      setForm((f) => ({ ...f, informative: false }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIsPostNF, form.budget_id]);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat((form.amount || "").toString().replace(",", "."));
    if (!(amt > 0)) { toast.error("Informe um valor válido"); return; }
    if (needsSchool && !form.school_id) { toast.error("Selecione a escola"); return; }
    setSaving(true);
    try {
      await api.post("/expenses", {
        date: form.date,
        category: form.category,
        amount: amt,
        description: form.description,
        school_id: showSchool ? (form.school_id || null) : null,
        budget_id: showSchool ? (form.budget_id || null) : null,
        informative: showSchool && !!form.informative && !!form.budget_id,
        payment_method: showPayment ? form.payment_method : "caixa",
        installments: showPayment && form.payment_method === "cartao" ? Number(form.installments) || 1 : 1,
        receipt_path: form.receipt_path || "",
      });
      toast.success(form.informative ? "Lançamento informativo salvo" : "Despesa lançada");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao lançar despesa");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="exp-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-black text-[#1e3a8a]">Nova despesa</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <CategoryPicker value={form.category} onChange={(k) => setForm({ ...form, category: k, school_id: "", budget_id: "", informative: false, payment_method: "caixa", installments: 1 })} />
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Data</label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full min-w-0 h-10 appearance-none" data-testid="exp-date" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Valor (R$)</label>
              <MoneyInput
                value={form.amount}
                onChange={(v) => setForm({ ...form, amount: v })}
                className="w-full"
                data-testid="exp-amount"
              />
            </div>
          </div>
          {showPayment && <PaymentMethodField form={form} setForm={setForm} withDiaADia={!!form.budget_id} caixaHint={form.budget_id ? "Sai do Caixa da empresa agora." : "Despesa geral: aparece só no relatório mensal (Saídas diversas)."} />}
          {form.category === "impostos" && (
            <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-2" data-testid="exp-tax-hint">
              ISS e Simples já são calculados automaticamente ao fechar cada orçamento. Use aqui só para outros impostos/taxas avulsos.
            </p>
          )}
          {showSchool && (
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">
              Escola {needsSchool ? "" : <span className="text-slate-400 font-normal">(opcional, para vincular ao orçamento)</span>}
            </label>
            <SearchableCombobox
              value={form.school_id}
              onChange={(v) => setForm({ ...form, school_id: v, budget_id: "", informative: false })}
              options={schoolOptions}
              placeholder={eligibleSchoolIds === null ? "Carregando..." : (schoolOptions.length ? "Selecione uma escola" : "Nenhuma escola elegível")}
              searchPlaceholder="Buscar escola..."
              emptyLabel="Nenhuma escola encontrada"
              data-testid="exp-school"
            />
            <p className="text-[10px] text-slate-500 mt-1 leading-snug">
              Lista limitada a escolas com orçamentos nos status: <b>Aprovado</b>, <b>Em andamento</b>, <b>Finalizado</b>
              {showPostNF && <> + <b>Nota gerada/paga</b></>}.
            </p>
          </div>
          )}
          {/* Toggle for post-NF budgets */}
          {canLinkBudget && (
            <div className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-2">
              <Checkbox
                id="show-post-nf"
                checked={showPostNF}
                onCheckedChange={(v) => { setShowPostNF(!!v); setForm((f) => ({ ...f, budget_id: "", informative: false })); }}
                data-testid="exp-show-post-nf"
              />
              <label htmlFor="show-post-nf" className="text-xs text-slate-700 leading-snug cursor-pointer">
                Também ver orçamentos com <b>nota já gerada/paga</b>
                <span className="block text-[10px] text-slate-500">
                  Esses só podem receber lançamentos informativos (não entram nos cálculos).
                </span>
              </label>
            </div>
          )}
          {/* Budget picker */}
          {canLinkBudget && (
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">
                Orçamento <span className="text-slate-400 font-normal">(opcional)</span>
              </label>
              <SearchableCombobox
                value={form.budget_id}
                onChange={(v) => setForm({ ...form, budget_id: v })}
                options={[{ value: "", label: "Despesa geral (sem orçamento)", sub: "" }, ...budgetOpts]}
                placeholder="Despesa geral desta escola"
                searchPlaceholder="Buscar nº, serviço..."
                emptyLabel={budgetOpts.length ? "Sem resultados" : "Esta escola não tem orçamentos nos status selecionados"}
                data-testid="exp-budget"
              />
              <p className="text-[10px] text-slate-500 mt-1 leading-snug">
                Despesa vinculada a um orçamento é deduzida no resultado dele e dos sócios.
              </p>
            </div>
          )}
          {/* Informative-mode banner — only when a budget is picked (esp. post-NF) */}
          {canLinkBudget && form.budget_id && (
            <div
              className={`rounded-md border px-2.5 py-2 flex items-start gap-2 ${
                selectedIsPostNF
                  ? "bg-amber-50 border-amber-300 text-amber-900"
                  : "bg-slate-50 border-slate-200 text-slate-700"
              }`}
            >
              <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${selectedIsPostNF ? "text-amber-600" : "text-slate-400"}`} />
              <div className="min-w-0 flex-1">
                <label className="flex items-start gap-2 cursor-pointer">
                  <Checkbox
                    checked={form.informative}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, informative: !!v }))}
                    disabled={selectedIsPostNF}  // post-NF budgets MUST be informative
                    data-testid="exp-informative"
                  />
                  <span className="text-xs leading-snug">
                    <b>Lançamento informativo</b> — não entra nos cálculos
                    {selectedIsPostNF && <span className="block text-[10px] opacity-80">Obrigatório: este orçamento já teve a nota gerada/paga.</span>}
                    {!selectedIsPostNF && <span className="block text-[10px] opacity-80">Marque se este lançamento é só para histórico e NÃO deve alterar o resultado nem os valores dos sócios.</span>}
                  </span>
                </label>
              </div>
            </div>
          )}
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Descrição</label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={DESC_PLACEHOLDER[form.category] || "Descrição"} data-testid="exp-desc" />
          </div>
          <ReceiptField value={form.receipt_path} onChange={(p) => setForm((f) => ({ ...f, receipt_path: p }))} testid="exp-receipt" />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white" data-testid="exp-submit">
              {saving ? "Salvando..." : "Lançar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
